import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Gate component for routes that require an authenticated user.
 *
 * Behaviour:
 * - Auth not configured → redirect to `/`. The Sign-in button will already be
 *   disabled, so this is purely a defence in depth.
 * - Auth still loading → render nothing (parent Suspense / spinner is fine).
 * - Authenticated → render children.
 * - Not authenticated → trigger Auth0 redirect, preserving the target path.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isConfigured, isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isConfigured && !isLoading && !isAuthenticated) {
      void login(location.pathname + location.search);
    }
  }, [isConfigured, isLoading, isAuthenticated, login, location]);

  if (!isConfigured) {
    return <Navigate to="/" replace />;
  }
  if (isLoading || !isAuthenticated) {
    return null;
  }
  return <>{children}</>;
}
